<template>
  <v-container fluid class="px-3 pt-2">
    <!-- Action Buttons (Always Visible) -->
    <v-layout row class="mb-2">
      <v-btn
        style="background-color: #fff !important;"
        outline
        color="success"
        @click="draftDialog = true"
      >
        <v-icon left>mdi-file</v-icon>
        Drafts ({{ getDraftsCount() }})
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="success" @click="newBroadcast()">
        <v-icon left>mdi-bullhorn-outline</v-icon>
        New Broadcast
      </v-btn>
    </v-layout>

    <!-- Loading Progress Bar -->
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <!-- Main Content (Hidden During Loading) -->
    <div v-if="!loading">
      <!-- Active and Scheduled Broadcasts -->
      <v-layout row class="mb-4">
        <!-- Active Broadcasts -->
        <v-flex xs12 md6 class="mr-2">
          <v-card class="pa-2" elevation="2">
            <v-card-title class="pa-2 title font-weight-bold">
              <v-icon class="mr-2" left large color="success"
                >mdi-cached</v-icon
              >
              Active Broadcasts ({{ activeBroadcasts.length || 0 }})
            </v-card-title>
            <div v-if="activeBroadcasts.length">
              <v-layout
                row
                v-for="broadcast in activeBroadcasts"
                :key="broadcast.id"
                class="pa-2 mb-2"
                @click="onRowClicked(broadcast)"
                style="cursor: pointer; border: 2px solid #5cb860; margin: 0px 10px 0px 10px"
              >
                <v-icon
                  large
                  class="mr-3"
                  :color="getBroadcstIconColor(broadcast)"
                  >{{ getBroadcstIcon(broadcast) }}</v-icon
                >
                <v-layout row justify-space-between>
                  <div>
                    <div class="font-weight-medium mb-1 mt-1">
                      {{ broadcast.broadcastName }}
                    </div>
                    <p class="mb-1">
                      <span class="font-weight-medium">By User:</span>
                      {{ broadcast.userName }}
                    </p>
                    <p
                      class="mb-1 width-300"
                      v-if="broadcast.type == 'MESSAGE'"
                      :title="broadcast.text"
                    >
                      <span class="font-weight-medium">Text:</span>
                      {{ truncateText(broadcast.text) }}
                    </p>
                    <p class="mb-0 mt-2" v-if="broadcast.type == 'CALL'">
                      <audio
                        style="height: 30px;"
                        controls
                        :src="broadcast.audioFile"
                      ></audio>
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Source:</span>
                      {{ broadcast.source }}
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Carrier:</span>
                      {{ broadcast.carrier }}
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">From Number:</span>
                      {{ broadcast.fromNumber }}
                    </p>
                  </div>
                  <div>
                    <p class="mb-1 mt-2 width-280">
                      <span class="font-weight-medium">Broadcast Time:</span>
                      {{ broadcast.broadcastTime | time }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Queue Status:</span>
                      {{ broadcast.broadcastStatus }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Total:</span>
                      {{ broadcast.totalCount }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Completed:</span>
                      {{ broadcast.completed }}
                    </p>

                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">In Process:</span>
                      {{ broadcast.inProcess }}
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Queued:</span>
                      {{ broadcast.queued }}
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Entered On:</span>
                      {{ broadcast.date | time }}
                    </p>
                  </div>
                </v-layout>
                <v-btn
                  @click.stop="toggleExpanded(broadcast, 'SCHEDULED')"
                  class="ml-3"
                  flat
                  icon
                  color="success"
                >
                  <v-icon>{{
                    broadcast.expanded ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </v-btn>
              </v-layout>
            </div>
            <p v-else class="ml-2 pl-1">
              No active broadcasts.
            </p>
          </v-card>
        </v-flex>

        <!-- Scheduled Broadcasts -->
        <v-flex xs12 md6 class="ml-2">
          <v-card class="pa-2" elevation="2">
            <v-card-title class="pa-2 title font-weight-bold">
              <v-icon class="mr-2" left large color="#fdb400"
                >mdi-calendar-clock</v-icon
              >
              Scheduled Broadcasts ({{ scheduledBroadcasts.length || 0 }})
            </v-card-title>
            <div v-if="scheduledBroadcasts.length">
              <v-layout
                row
                v-for="broadcast in scheduledBroadcasts"
                :key="broadcast.id"
                class="pa-2 mb-2"
                @click="onRowClicked(broadcast)"
                style="cursor: pointer; border: 2px solid #5cb860; margin: 0px 10px 0px 10px"
              >
                <v-icon
                  large
                  class="mr-3"
                  :color="getBroadcstIconColor(broadcast)"
                  >{{ getBroadcstIcon(broadcast) }}</v-icon
                >
                <v-layout row justify-space-between>
                  <div>
                    <div
                      style="
                        background-color: #f44336;
                        padding-top: 5px !important;
                        border-radius: 5px;
                        max-width: fit-content !important;
                    "
                      class="caption py-1 px-2 white--text"
                      v-if="isPending(broadcast)"
                    >
                      PENDING
                    </div>
                    <div class="font-weight-medium mb-1 mt-1">
                      {{ broadcast.broadcastName }}
                    </div>
                    <p class="mb-1">
                      <span class="font-weight-medium">By User:</span>
                      {{ broadcast.userName }}
                    </p>
                    <p
                      class="mb-1 width-300"
                      v-if="broadcast.type == 'MESSAGE'"
                      :title="broadcast.text"
                    >
                      <span class="font-weight-medium">Text:</span>
                      {{ truncateText(broadcast.text) }}
                    </p>
                    <p class="mb-0 mt-2" v-if="broadcast.type == 'CALL'">
                      <audio
                        style="height: 30px;"
                        controls
                        :src="broadcast.audioFile"
                      ></audio>
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Source:</span>
                      {{ broadcast.source }}
                    </p>
                  </div>
                  <div>
                    <p class="mb-1 mt-2 width-280">
                      <span class="font-weight-medium">Broadcast Time:</span>
                      {{ broadcast.broadcastTime | time }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Total:</span>
                      {{ broadcast.totalCount }}
                    </p>
                    <p class="mb-1" v-if="broadcast.expanded">
                      <span class="font-weight-medium">Entered On:</span>
                      {{ broadcast.date | time }}
                    </p>
                  </div>
                </v-layout>
                <v-btn
                  @click.stop="toggleExpanded(broadcast, 'SCHEDULED')"
                  class="ml-3"
                  flat
                  icon
                  color="success"
                >
                  <v-icon>{{
                    broadcast.expanded ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </v-btn>
              </v-layout>
            </div>
            <p v-else class="ml-2 pl-1">
              No scheduled broadcasts.
            </p>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Completed Broadcasts -->
      <v-layout row>
        <v-flex xs12>
          <v-card class="pa-2" elevation="2">
            <v-layout row justify-space-between>
              <v-card-title
                style="margin-bottom: 0px !important;"
                class="title px-2 py-0 mb-0 font-weight-bold"
              >
                <v-icon class="mr-2" left large color="success"
                  >mdi-check-circle</v-icon
                >
                <v-layout column>
                  <div class="pt-2 pb-0 mb-0">
                    Completed Broadcasts ({{ completedBroadcasts.length }})
                  </div>
                  <div class="caption py-0 mt-0">
                    Fitlered for the Last 2 Months
                  </div>
                </v-layout>
              </v-card-title>
              <v-spacer></v-spacer>
              <v-btn
                style="height: 28px;"
                outline
                color="success"
                class="mr-2"
                small
                @click="viewOlder"
                >Older<v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-layout>
            <v-layout row class="mb-2 mt-0">
              <v-spacer></v-spacer>
              <v-menu
                v-model="datePickerMenu"
                :close-on-content-click="true"
                offset-y
                full-width
                prepend-icon="mdi-calendar"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="fromDate"
                    :value="fromDate"
                    label="From Time"
                    prepend-icon="mdi-calendar"
                    clearable
                    v-on="on"
                    class="mx-2 mt-0 pb-1 pt-0"
                    style="max-width: 200px"
                    @input="fromDateChanged(fromDate)"
                    hide-details
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="fromDate"
                  @input="fromDateChanged(fromDate)"
                ></v-date-picker>
              </v-menu>
              <v-select
                v-model="broadcastType"
                label="Type"
                :items="['CALL', 'MESSAGE', 'EMAIL']"
                clearable
                @change="broadcastTypeChanged"
                hide-details
                style="max-width: 250px;"
                class="mt-0 mr-2 pt-0"
              >
              </v-select>
              <v-text-field
                style="max-width: 250px;"
                v-model="search"
                label="Search"
                prepend-inner-icon="mdi-magnify"
                single-line
                hide-details
                clearable
                class="mt-0 mr-2 pt-0"
              ></v-text-field>
            </v-layout>
            <div class="mx-2">
              <v-data-table
                :headers="headers"
                :items="completedBroadcasts"
                :search="search"
                class="elevation-1"
                :rows-per-page-items="[10, 15, 20]"
                :pagination.sync="pagination"
              >
                <template v-slot:items="props">
                  <tr
                    style="cursor: pointer;"
                    @click="onRowClicked(props.item)"
                  >
                    <td class="py-0">
                      <v-icon
                        @click.stop="props.expanded = !props.expanded"
                        class=""
                        >{{
                          props.expanded ? "mdi-chevron-up" : "mdi-chevron-down"
                        }}</v-icon
                      >
                    </td>

                    <td>
                      <v-icon class="" :color="props.item.typeIconColor">{{
                        props.item.typeIcon
                      }}</v-icon>
                    </td>
                    <td :title="props.item.broadcastName">
                      {{ truncateText(props.item.broadcastName, 25) }}
                    </td>
                    <td>{{ props.item.type }}</td>
                    <td>{{ props.item.userName }}</td>
                    <td>
                      {{ props.item.broadcastTime | time }}
                    </td>

                    <td>{{ props.item.totalCount }}</td>
                    <td>{{ props.item.completed }}</td>
                    <td class="py-0">
                      <audio
                        style="height: 30px; padding-top: 4px;"
                        controls
                        v-if="props.item.type == 'CALL'"
                        :src="props.item.audioFile"
                      ></audio>
                      <div v-else :title="props.item.text">
                        {{ truncateText(props.item.text, 45) }}
                      </div>
                    </td>
                  </tr>
                </template>
                <template v-slot:expand="props">
                  <div class="pa-2">
                    <p class="mb-1">
                      <span class="font-weight-medium">Broadcast Status:</span>
                      {{ props.item.broadcastStatus }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Source:</span>
                      {{ props.item.source }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">From Number:</span>
                      {{ props.item.fromNumber }}
                    </p>
                    <p
                      class="mb-1"
                      v-if="props.item.date != props.item.broadcastTime"
                    >
                      <span class="font-weight-medium">Entered On:</span>
                      {{ props.item.date | time }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Carrier:</span>
                      {{ props.item.carrier }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Accounts Source:</span>
                      {{ props.item.accountsSource }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Completed:</span>
                      {{ props.item.completed || 0 }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Total:</span>
                      {{ props.item.totalCount || 0 }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">In Process:</span>
                      {{ props.item.inProcess || 0 }}
                    </p>
                    <p class="mb-1">
                      <span class="font-weight-medium">Queued:</span>
                      {{ props.item.queued || 0 }}
                    </p>
                    <p class="mb-1" v-if="props.item.delivered">
                      <span class="font-weight-medium">Delivered:</span>
                      {{ props.item.delivered }}
                    </p>
                    <p class="mb-1" v-if="props.item.new">
                      <span class="font-weight-medium">New:</span>
                      {{ props.item.new }}
                    </p>
                    <p class="mb-1" v-if="props.item.pending">
                      <span class="font-weight-medium">Pending:</span>
                      {{ props.item.pending }}
                    </p>
                    <p class="mb-1" v-if="props.item.sent">
                      <span class="font-weight-medium">Sent:</span>
                      {{ props.item.sent }}
                    </p>
                    <p class="mb-1" v-if="props.item.unknown">
                      <span class="font-weight-medium">Unknown:</span>
                      {{ props.item.unknown }}
                    </p>
                    <p class="mb-1" v-if="props.item.waiting">
                      <span class="font-weight-medium">Waiting:</span>
                      {{ props.item.waiting }}
                    </p>
                    <p class="mb-1" v-if="props.item.undelivered">
                      <span class="font-weight-medium">Undelivered:</span>
                      {{ props.item.undelivered }}
                    </p>
                    <p class="mb-1" v-if="props.item.failed">
                      <span class="font-weight-medium">Failed:</span>
                      {{ props.item.failed }}
                    </p>
                    <p class="mb-1" v-if="props.item.error">
                      <span class="font-weight-medium">Error:</span>
                      {{ props.item.error }}
                    </p>
                    <p class="mb-1" v-if="props.item.text">
                      <span class="font-weight-medium">Text:</span>
                      {{ props.item.text }}
                    </p>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card>
        </v-flex>
      </v-layout>
    </div>

    <!-- Drafts Dialog -->
    <v-dialog v-model="draftDialog" max-width="800">
      <v-card class="pa-3">
        <v-layout row justify-space-between>
          <v-card-title class="headline pl-0 pt-0"
            >Drafts ({{ getDraftsCount() }})</v-card-title
          >
        </v-layout>
        <v-data-table
          :headers="draftHeaders"
          :items="draftBroadcasts"
          :search="draftSearch"
          :sort-by="[{ key: 'date', order: 'desc' }]"
          class="elevation-1"
        >
          <template v-slot:headers="props">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.text"
                class="text-xs-left"
              >
                {{ header.text }}
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <tr style="cursor: pointer;">
              <td>{{ props.item.broadcastName }}</td>
              <td>{{ props.item.type }}</td>
              <td>{{ props.item.userName }}</td>
              <td>{{ formatDate(props.item.date) }}</td>
              <td>
                <v-btn
                  color="success"
                  small
                  class="my-0"
                  @click="onRowClicked(props.item, true)"
                  >Open</v-btn
                >
              </td>
              <td class="py-0 my-0">
                <v-icon
                  @click.stop="openDeleteDraftDialog(props.item)"
                  class="mr-2"
                  color="success"
                  >mdi-close</v-icon
                >
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-card-actions class="pb-0 mr-0 mt-2">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="draftDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Draft Confirmation Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline pb-0">Confirm Delete</v-card-title>
        <v-card-text class="font-weight-medium">
          Are you sure you want to delete the selected draft?
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="confirmDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="success" :disabled="processing" @click="deleteDrafts">
            {{ processing ? "Deleting..." : "Delete" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import { truncate } from "../../utils/helpers.js";

export default {
  data: () => ({
    broadcasts: [],
    allBroadcasts: [],
    draftBroadcasts: [],
    loading: false,
    activeTab: "completed",
    selectedDrafts: [],
    confirmDeleteDialog: false,
    processing: false,
    draftDialog: false,
    search: "",
    broadcastType: "",
    datePickerMenu: false,
    fromDate: "",
    draftSearch: "",

    pagination: {
      sortBy: "broadcastTime",
      descending: true,
    },

    headers: [
      { text: "", value: "checkbox" },
      { text: "", value: "typeIcon" },
      { text: "Name", value: "broadcastName", sortable: true },
      { text: "Type", value: "type", sortable: true },
      { text: "User ", value: "userName", sortable: true },
      { text: "Time", value: "broadcastTime", sortable: true },
      { text: "Total", value: "totalCount", sortable: true },
      { text: "Completed", value: "completed", sortable: true },
      { text: "Action", value: "action" },
    ],
    draftHeaders: [
      { text: "Name", value: "broadcastName", sortable: true },
      { text: "Type", value: "type", sortable: true },
      { text: "User ", value: "userName", sortable: true },
      { text: "Date", value: "date", sortable: true },
    ],
  }),
  computed: {
    activeBroadcasts() {
      return this.broadcasts.filter((b) => b.status === "ACTIVE");
    },
    scheduledBroadcasts() {
      return this.broadcasts
        .filter((b) => b.status === "SCHEDULED")
        .sort((a, b) => {
          if (Date.parse(a.broadcastTime) < Date.parse(b.broadcastTime))
            return -1;
          if (Date.parse(a.broadcastTime) > Date.parse(b.broadcastTime))
            return 1;
          return 0;
        });
    },
    completedBroadcasts() {
      return this.broadcasts.filter((b) => b.status === "COMPLETED");
    },
  },
  created() {
    this.load();
    this.loadDrafts();
  },
  methods: {
    async load() {
      this.loading = true;
      await this.loadBroadcasts();
      this.loading = false;
    },
    async loadBroadcasts() {
      try {
        const response = await this.$http.get(
          "api/broadcastReport/getBroadcasts"
        );
        this.broadcasts = response
          .map((broadcast) => {
            broadcast.expanded = false;
            broadcast.typeIcon = this.getBroadcstIcon(broadcast);
            broadcast.typeIconColor = this.getBroadcstIconColor(broadcast);

            return broadcast;
          })
          .sort((a, b) => {
            if (Date.parse(a.broadcastTime) > Date.parse(b.broadcastTime))
              return -1;
            if (Date.parse(a.broadcastTime) < Date.parse(b.broadcastTime))
              return 1;
            return 0;
          });
        this.allBroadcasts = this.broadcasts;
      } catch (error) {
        console.error("Error loading broadcasts:", error);
      }
    },
    async loadDrafts() {
      try {
        const response = await this.$http.get("odata/broadcastDrafts");
        this.draftBroadcasts = response.value;
      } catch (error) {
        console.error("Error loading drafts:", error);
      }
    },
    formatDate(date) {
      return moment(date).format("MM/DD/YYYY h:mm:ss A");
    },
    newBroadcast(id = null) {
      this.$router.push({
        name: "NewBroadcast",
        params: { id: id },
      });
    },
    getBroadcstIcon(broadcast) {
      switch (broadcast.type) {
        case "MESSAGE":
          return "mdi-message-processing";
        case "CALL":
          return "mdi-phone";
        case "EMAIL":
          return "mdi-email";
        default:
          return "";
      }
    },
    getBroadcstIconColor(broadcast) {
      switch (broadcast.type) {
        case "MESSAGE":
          return "rgb(48 108 162)";
        case "CALL":
          return "success";
        case "EMAIL":
          return "#bc56a5";
        default:
          return "";
      }
    },
    onRowClicked(row, isDraft = false) {
      if (isDraft) {
        this.newBroadcast(row.id);
        return;
      }
      if (row.type === "CALL") {
        this.$router.push({
          name: "BroadcastCalls",
          params: { id: row.id },
        });
      } else if (row.type === "EMAIL") {
        this.$router.push({
          name: "BroadcastEmails",
          params: { id: row.id },
        });
      } else {
        this.$router.push({
          name: "BroadcastMessages",
          params: { id: row.id },
        });
      }
    },
    isPending(broadcast) {
      return new Date(broadcast.broadcastTime) < new Date();
    },
    toggleExpanded(broadcast, status) {
      this.broadcasts
        .filter((b) => b.status == status && b.id != broadcast.id)
        .forEach((b) => (b.expanded = false));

      broadcast.expanded = !broadcast.expanded;
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    truncateText(text, count = 80) {
      return truncate(text, count);
    },
    viewOlder() {
      this.$router.push({
        name: "Broadcasts",
      });
    },
    broadcastTypeChanged(value) {
      if (!value) {
        this.broadcasts = this.allBroadcasts;

        return;
      }

      this.broadcasts = this.allBroadcasts.filter((b) => b.type == value);
    },
    fromDateChanged(value) {
      if (!value) {
        this.broadcasts = this.allBroadcasts;

        return;
      }

      const date = new Date(value);

      this.broadcasts = this.allBroadcasts.filter(
        (a) => new Date(a.broadcastTime) >= date
      );
    },
    openDeleteDraftDialog(broadcast) {
      this.selectedDrafts = [broadcast];
      this.confirmDeleteDialog = true;
    },
    async deleteDrafts() {
      try {
        this.processing = true;
        await this.$http.post(
          "api/broadcast/deleteDraft",
          this.selectedDrafts.map((s) => s.id)
        );
        this.confirmDeleteDialog = false;
        this.selectedDrafts = [];
        await this.loadDrafts();
      } catch (error) {
        console.error("Error deleting drafts:", error);
      } finally {
        this.processing = false;
      }
    },
    getDraftsCount() {
      return this.draftBroadcasts.length || 0;
    },
  },
};
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
</style>
